import React, { useState, useEffect } from "react";
import adminClasses from "../../../landing-pages/admin/admin.module.css";
import IdNameModel from "../../../../models/common/IdNameModel";
import modalUtils from "../../../../utils/modal-utils";
import loaderUtils from "../../../../utils/loader-utils";
import adminCompanyService from "../../../../services/admin/admin-company-service";
import toasterUtils from "../../../../utils/toaster-utils";
import AddProfilesComponent from "../../../../components/company/add-profiles/AddProfilesComponent";
import jobPostingService from "../../../../services/company/job-posting-service";
import addProfilesService from "../../../../services/company/add-profiles-service";
import { Autocomplete, Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { GuidEnum } from "../../../../models/enums/GuidEnum";

interface AdminAddProfilesProps {
    companiesList: IdNameModel[];
    vendorList: IdNameModel[];
    statusList: IdNameModel[];
};

const AdminAddProfilesComponent = (props: AdminAddProfilesProps) => {
    const [recruiterList, setRecruiterList] = useState<IdNameModel[]>([]);
    const [jobcodeList, setJobcodeList] = useState<IdNameModel[]>([]);
    const [companySearch, setCompanySearch] = useState<string>(GuidEnum.Empty);
    const [recruiterSearch, setRecruiterSearch] = useState<string>(GuidEnum.Empty);
    const [jobcodeSearch, setJobcodeSearch] = useState<string>(GuidEnum.Empty);
    const [vendorList, setVendorList] = useState<IdNameModel[]>([]);
    const [refreshRequirements, setRefreshRequirements] = useState<number>(2);
    const [canScheduleInterviews, setCanScheduleInterviews] = useState<boolean>(false);
    const [refreshCanScheduleInterviews, setRefreshCanScheduleInterviews] = useState<number>(2);
    const [refreshVendors, setRefreshVendors] = useState<number>(2);

    useEffect(() => {
        clearSearch();
    }, []);

    const clearSearch = () => {
        setCompanySearch(GuidEnum.Empty)
        setRecruiterSearch(GuidEnum.Empty);
        setJobcodeSearch(GuidEnum.Empty);
        setRefreshRequirements(2);
    };

    const loadRecruiters = (companyId: string) => {
        setRefreshRequirements(2);
        setCompanySearch(companyId);
        modalUtils.showLoader();
        adminCompanyService.getRecruiterByCompany(companyId).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setRecruiterSearch(GuidEnum.Empty);
            setRecruiterList(res.items);
        }).catch((error) => {
            setRecruiterSearch(GuidEnum.Empty);
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const loadJobcodes = (recruiterId: string) => {
        setRefreshRequirements(2);
        setRecruiterSearch(recruiterId);
        modalUtils.showLoader();
        adminCompanyService.getJobCodesForRecruiter(recruiterId).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setJobcodeSearch(GuidEnum.Empty);
            setJobcodeList(res.items);
        }).catch((error) => {
            setRecruiterSearch(GuidEnum.Empty);
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const setJobcode = (requirementId: string) => {
        setRefreshRequirements(2);
        setJobcodeSearch(requirementId);
        getJobPosting(requirementId);
        getVendorList(companySearch, requirementId);
    };

    const getRequirements = () => {
        if (companySearch === "" || companySearch === GuidEnum.Empty || recruiterSearch === "" || recruiterSearch === GuidEnum.Empty) {
            setRefreshRequirements(2);
        } else {
            setRefreshRequirements(refreshRequirements * 5);
        }
    };

    const getJobPosting = (requirementId: string) => {
        modalUtils.showLoader();
        setRefreshCanScheduleInterviews(2);
        jobPostingService.getJobPostingById(requirementId).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            if (res.data.isCalibrationCallCompleted && res.data.isClientApproved && res.data.panelsAvailable) {
                setCanScheduleInterviews(true);
            }
            setRefreshCanScheduleInterviews(refreshCanScheduleInterviews * 5);
        }).catch((error) => {
            modalUtils.closeModal();
            setRefreshCanScheduleInterviews(refreshCanScheduleInterviews * 5);
            toasterUtils.showError(error);
        });
    };

    const getVendorList = (companyId: string, requirementId: string) => {
        addProfilesService.fillVendorsForAddProfiles(companyId, requirementId).then((res) => {
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setVendorList(res.items);
            setRefreshVendors(refreshVendors * 5);
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    return (
        <div>
            <div className="card card-white">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <FormControl fullWidth margin="none" size="small">
                                <Autocomplete options={props.companiesList} value={props.companiesList.find((x) => x.guId === companySearch) || null}
                                    onChange={(_, newValue) => loadRecruiters(newValue ? newValue.guId : GuidEnum.Empty)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Company" size="small" margin="normal" fullWidth required />
                                    )}
                                />
                            </FormControl>
                        </div>
                        <div className="col-md-3">
                            <FormControl fullWidth margin="none" size="small">
                                <Autocomplete options={recruiterList} value={recruiterList.find((x) => x.guId === recruiterSearch) || null}
                                    onChange={(_, newValue) => loadJobcodes(newValue ? newValue.guId : GuidEnum.Empty)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Recruiter" size="small" margin="normal" fullWidth required />
                                    )}
                                />
                            </FormControl>
                        </div>
                        <div className="col-md-3">
                            <FormControl fullWidth margin="none" size="small">
                                <Autocomplete options={jobcodeList} value={jobcodeList.find((x) => x.guId === jobcodeSearch) || null}
                                    onChange={(_, newValue) => setJobcode(newValue ? newValue.guId : GuidEnum.Empty)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                    renderInput={(params) => (
                                        <TextField {...params} label="JobCode" size="small" margin="normal" fullWidth required />
                                    )}
                                />
                            </FormControl>
                        </div>
                        <div className="col-md-3">
                            <Button variant="contained" color="success" onClick={() => getRequirements()} startIcon={<SearchIcon />} sx={{ ml: 2, mt: 2 }}>
                                Search
                            </Button>
                            <Button variant="contained" color="warning" onClick={() => clearSearch()} startIcon={<ClearIcon />} sx={{ ml: 2, mt: 2 }}>
                                Clear
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            {refreshRequirements > 2 && refreshCanScheduleInterviews > 2 &&
                <AddProfilesComponent
                    vendorList={vendorList}
                    statusList={props.statusList}
                    requirementId={jobcodeSearch}
                    profileId={recruiterSearch}
                    userId={recruiterSearch}
                    canScheduleInterviews={canScheduleInterviews} />
            }
        </div>
    );
};
export default AdminAddProfilesComponent;